/**
 * Contract response -> Cytoscape elements for the consolidated view. Pure.
 * Rules: the context bucket is never drawn (it is summarised as a count);
 * edges touching an undrawn or unknown bucket are dropped; amounts are shown
 * per token and never summed across tokens (R15 spirit).
 */
import type { ElementDefinition } from 'cytoscape';
import type { ConsolidatedBucket, ConsolidatedResponse } from './types';
import { bucketMoneyLine, formatByToken } from './format';

export interface ConsolidatedElements {
  elements: ElementDefinition[];
  /** Ids of buckets that start the left-to-right layout. */
  rootIds: string[];
  /** Wallets folded into the undrawn context bucket. */
  collapsedWallets: number;
}

const ROOT_ROLES = new Set(['depositors', 'sources', 'funding_exchange']);

function nodeSize(b: ConsolidatedBucket): number {
  const usd = Math.max(Number(b.totals?.in_usd) || 0, Number(b.totals?.out_usd) || 0);
  return Math.round(Math.min(96, 52 + Math.log10(1 + usd) * 8));
}

export function bucketSubtitle(b: ConsolidatedBucket): string {
  const tokens = bucketMoneyLine(b.totals);
  const members = `${b.member_count} wallet${b.member_count === 1 ? '' : 's'}`;
  return tokens ? `${members} · ${tokens}` : members;
}

export function edgeLabel(transfers: number, byToken: Record<string, number>): string {
  const amt = formatByToken(byToken);
  const n = `${transfers} transfer${transfers === 1 ? '' : 's'}`;
  return amt ? `${n} · ${amt}` : n;
}

export function toElements(data: ConsolidatedResponse): ConsolidatedElements {
  const drawn = data.buckets.filter((b) => b.role !== 'context');
  const drawnIds = new Set(drawn.map((b) => b.id));
  const ctx = data.buckets.find((b) => b.role === 'context');
  const collapsedWallets = Math.max(data.limits?.context_collapsed ?? 0, ctx?.member_count ?? 0);

  const nodes: ElementDefinition[] = drawn.map((b) => ({
    group: 'nodes',
    data: {
      id: b.id,
      role: b.role,
      label: `${b.label}\n${bucketSubtitle(b)}`,
      size: nodeSize(b),
    },
  }));
  const edges: ElementDefinition[] = data.edges
    .filter((e) => drawnIds.has(e.source) && drawnIds.has(e.target))
    .map((e) => ({
      group: 'edges',
      data: {
        id: e.id,
        source: e.source,
        target: e.target,
        kind: e.kind,
        label: edgeLabel(e.transfers, e.total_by_token),
        width: Math.round((1.5 + Math.log10(1 + Math.max(0, e.transfers)) * 3) * 10) / 10,
      },
    }));
  const rootIds = drawn.filter((b) => ROOT_ROLES.has(b.role)).map((b) => b.id);
  return { elements: [...nodes, ...edges], rootIds, collapsedWallets };
}
